import React from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'react-qr-code';
import { formatDate, formatTime } from '../../helpers/formatDate';
import formatPin from '../../helpers/formatPin';
import './ParkingPassPrint.scss';

// One sheet per game, so a holder without a phone can carry the season on paper.
//
// The two layouts follow what that holder already sees in the app, because a
// steward comparing paper against a screen should not have to translate between
// two designs:
//   uses_external_qr -> the BernExpo permit (QR is scanned at the barrier)
//   otherwise        -> the regular card (staff read it by eye; no barrier)
//
// Paper-first rather than a screenshot of the screen version: the app draws
// those blocks white-on-#1c1c1c, which prints as slabs of toner and is what the
// client objected to. The wording and the order of the blocks are unchanged.
//
// The app's staff block is dropped entirely. It exists to host the confirm
// slider and says the confirmation is done by SCB personnel — instructions for a
// gesture that paper does not have.

const Sheet = ({ children, last }) => (
    <div className={`pkprint-sheet${last ? ' pkprint-sheet-last' : ''}`}>{children}</div>
);

// Header repeated on every sheet: whoever picks a page off the printer has to be
// able to tell whose card it is and which lot it opens.
const SheetHead = ({ pass, holderName }) => (
    <div className="pkprint-head">
        <span className="pkprint-head-field">{pass.field.name}</span>
        <span className="pkprint-head-meta">
            {formatPin(pass.assign_token)}
            {holderName ? ` · ${holderName}` : ''}
        </span>
    </div>
);

const GameLine = ({ game }) => (
    <>
        <p className="pkprint-strong">SC Bern{game.away_team?.name ? ` – ${game.away_team.name}` : ''}</p>
        {!!game.date && (
            <p>
                {formatDate(game.date)}, {formatTime(game.date)}
                {game.location ? <><br />{game.location}</> : null}
            </p>
        )}
    </>
);

// BernExpo permit: the QR carries everything, so the sheet stays close to the
// printed permit BernExpo itself issues.
const BernExpoSheet = ({ pass, game, holderName, last }) => {
    const day = game.date ? new Date(game.date) : null;
    const nextDay = day ? new Date(day.getTime() + 24 * 60 * 60 * 1000) : null;

    return (
        <Sheet last={last}>
            <SheetHead pass={pass} holderName={holderName} />

            <div className="pkprint-body pkprint-body-center">
                <div className="pkprint-title">BERNEXPO PARKING</div>

                {game.qr_payload && (
                    <div className="pkprint-qr">
                        <QRCode size={220} value={game.qr_payload} viewBox="0 0 256 256" />
                    </div>
                )}

                {/* BernExpo's barrier only lets you out if you drove in on the
                    same permit — the one instruction a driver must read first. */}
                <div className="pkprint-checkin">CHECK IN FIRST!</div>

                <div className="pkprint-match">
                    <div className="pkprint-match-scb">SCB</div>
                    <div className="pkprint-match-word">MATCH</div>
                </div>

                <div className="pkprint-game">
                    <GameLine game={game} />
                </div>

                <div className="pkprint-validity">
                    {day && nextDay
                        ? `Gültig ab ${formatDate(game.date)} 16:00 Uhr bis ${formatDate(
                              nextDay.toISOString(),
                          )} 04:00 Uhr.`
                        : 'Gültig ab 16:00 Uhr am Spieltag bis 04:00 Uhr am Folgetag.'}
                </div>
            </div>
        </Sheet>
    );
};

// The other five lots: no barrier and no scanner, so the code is printed as text
// under the QR as well — a steward can read it out if anything is ever queried.
const RegularSheet = ({ pass, game, holderName, last }) => (
    <Sheet last={last}>
        <SheetHead pass={pass} holderName={holderName} />

        <div className="pkprint-body">
            <div className="pkprint-title pkprint-title-left">PARKKARTE</div>

            {game.qr_payload && (
                <div className="pkprint-qr">
                    <QRCode size={190} value={game.qr_payload} viewBox="0 0 256 256" />
                    <span className="pkprint-qr-value">{game.qr_payload}</span>
                </div>
            )}

            <div className="pkprint-boxes">
                <div className="pkprint-box">
                    <h3>PARKPLATZ</h3>
                    <p className="pkprint-strong">{pass.field.name}</p>
                    {!!pass.field.entrance && (
                        <p className="pkprint-strong">Eingang: {pass.field.entrance}</p>
                    )}
                </div>

                <div className="pkprint-box">
                    <h3>SPIEL</h3>
                    <GameLine game={game} />
                </div>
            </div>

            {!!pass.field.info_text && (
                <div className="pkprint-box pkprint-box-wide">
                    <h3>INFORMATION</h3>
                    <p>{pass.field.info_text}</p>
                </div>
            )}
        </div>
    </Sheet>
);

/**
 * Print-only rendering of every access code on one parking pass.
 *
 * Hidden on screen and revealed by the print stylesheet, so the button is just
 * window.print() — no second route, and no endpoint handing out all the codes
 * of a pass outside the admin session.
 *
 * Rendered into a sibling of #root rather than in place. Printing then hides
 * #root outright, instead of hiding the admin element by element and floating
 * the sheets over it — that approach leaves the hidden layout occupying its
 * full height, which the printer faithfully reproduces as blank leading pages.
 * @component
 */
const ParkingPassPrint = ({ pass, holderName }) => {
    const games = pass.games || [];
    const external = !!pass.field.uses_external_qr;

    return createPortal(
        <div className="pkprint">
            {games.map((game, i) =>
                external ? (
                    <BernExpoSheet
                        key={game.access_code_id}
                        pass={pass}
                        game={game}
                        holderName={holderName}
                        last={i === games.length - 1}
                    />
                ) : (
                    <RegularSheet
                        key={game.access_code_id}
                        pass={pass}
                        game={game}
                        holderName={holderName}
                        last={i === games.length - 1}
                    />
                ),
            )}
        </div>,
        document.body,
    );
};

export default ParkingPassPrint;
